/**
 * CSV citizenship delta engine (pure).
 *
 * Classifies mapped import rows against existing items using Import API match
 * order (id → external_id → insert). Used by list-toolbar Excel/CSV import and
 * csvProfileRunner; does not invent a parallel identity model.
 */

export type CsvDeltaDisposition =
  | 'create'
  | 'update'
  | 'unchanged'
  | 'clarify'
  | 'skip'
  | 'missing';

export type CsvClarifyReason =
  | 'missing_title'
  | 'missing_external_id_on_update_candidate'
  | 'ambiguous_external_id'
  | 'id_not_found'
  | 'duplicate_external_id_in_file'
  | 'type_mismatch';

export interface CsvDeltaExistingItem {
  id: string;
  external_id?: string | null;
  /** Snapshot of comparable fields (stringified values). */
  fields: Record<string, string | null>;
}

export interface CsvDeltaRowResult {
  rowIndex: number;
  disposition: CsvDeltaDisposition;
  clarifyReason?: CsvClarifyReason;
  matchItemId?: string;
  externalId?: string;
  /** Fields present on the row that differ from the matched item (update path). */
  changedFields?: string[];
  beforeFields?: Record<string, string | null>;
  afterFields?: Record<string, string | null>;
  row: Record<string, unknown>;
}

const COMPARABLE_KEYS = [
  'title',
  'description',
  'itemCategory',
  'metaCategory',
  'strategicBucket',
  'altitudeLevel',
  'source',
  'tags',
  'desiredOutcome',
  'beneficiary',
  'evidenceOfSuccess',
  'whyNow',
  'obstacles',
  'work_type',
  'launch_type',
  'investment_type',
  'exit_status',
  'parentItemId',
] as const;

function normalizeScalar(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) {
    const parts = value.map((v) => String(v).trim()).filter(Boolean).sort();
    return parts.length ? parts.join(',') : null;
  }
  const s = String(value).trim();
  return s === '' ? null : s;
}

export function snapshotComparableFields(row: Record<string, unknown>): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const key of COMPARABLE_KEYS) {
    if (key in row) out[key] = normalizeScalar(row[key]);
  }
  return out;
}

function diffFields(
  incoming: Record<string, string | null>,
  existing: Record<string, string | null>,
): string[] {
  const changed: string[] = [];
  for (const key of Object.keys(incoming)) {
    const next = incoming[key];
    if (next === null) continue; // omitted / empty = leave unchanged (Import API partial PATCH)
    const prev = existing[key] ?? null;
    if (prev !== next) changed.push(key);
  }
  return changed;
}

/**
 * Classify mapped rows for a citizenship import run.
 * Rows without title are clarify (not silent skip) so they surface in Conflict Queue.
 */
export function classifyCsvDeltaRows(
  rows: Record<string, unknown>[],
  existing: CsvDeltaExistingItem[],
  options?: { externalKeyField?: string },
): CsvDeltaRowResult[] {
  const externalKeyField = options?.externalKeyField ?? 'external_id';
  const byId = new Map(existing.map((i) => [i.id, i]));
  const byExternal = new Map<string, CsvDeltaExistingItem[]>();
  for (const item of existing) {
    const ext = item.external_id?.trim();
    if (!ext) continue;
    const list = byExternal.get(ext) ?? [];
    list.push(item);
    byExternal.set(ext, list);
  }

  const seenExternalInFile = new Map<string, number>();
  const classified: CsvDeltaRowResult[] = rows.map((row, rowIndex) => {
    const title = normalizeScalar(row.title);
    const externalId =
      normalizeScalar(row[externalKeyField] ?? row.external_id) ?? undefined;
    const id = normalizeScalar(row.id) ?? undefined;

    if (externalId) {
      const prior = seenExternalInFile.get(externalId);
      if (prior != null) {
        return {
          rowIndex,
          disposition: 'clarify' as const,
          clarifyReason: 'duplicate_external_id_in_file' as const,
          externalId,
          row,
        };
      }
      seenExternalInFile.set(externalId, rowIndex);
    }

    if (!title) {
      return {
        rowIndex,
        disposition: 'clarify' as const,
        clarifyReason: 'missing_title' as const,
        externalId,
        row,
      };
    }

    if (id) {
      const hit = byId.get(id);
      if (!hit) {
        return {
          rowIndex,
          disposition: 'clarify' as const,
          clarifyReason: 'id_not_found' as const,
          externalId,
          row,
        };
      }
      const incoming = snapshotComparableFields(row);
      const changedFields = diffFields(incoming, hit.fields);
      return {
        rowIndex,
        disposition: changedFields.length === 0 ? ('unchanged' as const) : ('update' as const),
        matchItemId: hit.id,
        externalId: externalId ?? hit.external_id ?? undefined,
        changedFields,
        beforeFields: hit.fields,
        afterFields: incoming,
        row,
      };
    }

    if (externalId) {
      const hits = byExternal.get(externalId) ?? [];
      if (hits.length > 1) {
        return {
          rowIndex,
          disposition: 'clarify' as const,
          clarifyReason: 'ambiguous_external_id' as const,
          externalId,
          row,
        };
      }
      if (hits.length === 1) {
        const hit = hits[0];
        const incoming = snapshotComparableFields(row);
        const changedFields = diffFields(incoming, hit.fields);
        return {
          rowIndex,
          disposition: changedFields.length === 0 ? ('unchanged' as const) : ('update' as const),
          matchItemId: hit.id,
          externalId,
          changedFields,
          beforeFields: hit.fields,
          afterFields: incoming,
          row,
        };
      }
      return {
        rowIndex,
        disposition: 'create' as const,
        externalId,
        row,
      };
    }

    // No id / external_id → insert (Import API rule 3)
    return {
      rowIndex,
      disposition: 'create' as const,
      row,
    };
  });

  return classified;
}

/**
 * Flag existing records with an external key that did not appear in this file.
 * Never deletes — disposition is informational for the run record / UI.
 * Only runs when the file used external keys (otherwise "missing" is meaningless).
 */
export function appendMissingFromFile(
  results: CsvDeltaRowResult[],
  existing: CsvDeltaExistingItem[],
): CsvDeltaRowResult[] {
  const fileUsedExternalKeys = results.some((r) => Boolean(r.externalId));
  if (!fileUsedExternalKeys) return results;

  const seenExt = new Set(
    results.map((r) => r.externalId?.trim()).filter((x): x is string => Boolean(x)),
  );
  const seenIds = new Set(
    results.map((r) => r.matchItemId).filter((x): x is string => Boolean(x)),
  );

  const missing: CsvDeltaRowResult[] = [];
  let idx = results.length;
  for (const item of existing) {
    const ext = item.external_id?.trim();
    if (!ext) continue;
    if (seenExt.has(ext) || seenIds.has(item.id)) continue;
    missing.push({
      rowIndex: idx++,
      disposition: 'missing',
      matchItemId: item.id,
      externalId: ext,
      row: {
        title: item.fields.title,
        external_id: ext,
      },
    });
  }
  return [...results, ...missing];
}

export function summarizeCsvDelta(results: CsvDeltaRowResult[]): {
  create: number;
  update: number;
  unchanged: number;
  clarify: number;
  skip: number;
  missing: number;
} {
  const summary = { create: 0, update: 0, unchanged: 0, clarify: 0, skip: 0, missing: 0 };
  for (const r of results) summary[r.disposition] += 1;
  return summary;
}
